import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ImageComponent from '../../../components/common/Image';

const DASHBOARD_PATH = '/dashboard';

const HEADER_STYLES = `
  .los-hdr-search-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;border:0;border-radius:50%;background:transparent;color:#fff;cursor:pointer}
  .los-hdr-search-btn:hover{background:rgba(255,255,255,.16)}
  .los-hdr-search-btn:focus-visible{outline:2px solid #fff;outline-offset:2px}
  .los-hdr-search-dot{position:absolute;top:8px;right:8px;width:9px;height:9px;border-radius:50%;background:#ffd43b;border:2px solid #5a4be8}
  .los-hdr-search{position:fixed;inset:0;z-index:1060;display:flex;align-items:flex-start;justify-content:center;padding:72px 12px 12px}
  .los-hdr-search-backdrop{position:absolute;inset:0;background:rgba(16,28,56,.55)}
  .los-hdr-search-panel{position:relative;width:100%;max-width:640px;background:#fff;border-radius:14px;padding:14px 14px 12px;box-shadow:0 18px 48px rgba(16,28,56,.3)}
  .los-hdr-search-row{display:flex;align-items:center;gap:8px;border:1px solid #d7e0ee;border-radius:10px;padding:0 8px 0 12px;color:#5d6d85}
  .los-hdr-search-row:focus-within{border-color:#7ba7ef;box-shadow:0 0 0 3px rgba(123,167,239,.35)}
  .los-hdr-search-row input{flex:1;min-width:0;border:0;outline:0;background:transparent;padding:13px 0;font:inherit;font-size:14px;color:#172b4d;box-shadow:none}
  .los-hdr-search-row button{flex:0 0 auto;border:0;border-radius:8px;background:#edf3ff;color:#174ea6;padding:6px 10px;font:inherit;font-size:12px;font-weight:700;cursor:pointer}
  .los-hdr-search-hint{margin:8px 2px 0;font-size:12px;color:#5d6d85}
`;

const SearchIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
    </svg>
);
SearchIcon.propTypes = { size: PropTypes.number };

const Header = ({
    searchTerm = '',
    setSearchTerm,
    showSearchModal,
    setShowSearchModal,
    title = 'FAQs',
    searchPlaceholder = 'Search questions, errors, or keywords…',
}) => {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const triggerRef = useRef(null);

    // Works controlled (parent owns the flag) or standalone.
    const [localOpen, setLocalOpen] = useState(false);
    const isOpen = showSearchModal ?? localOpen;
    const setOpen = setShowSearchModal ?? setLocalOpen;

    const goToDashboard = (e) => {
        e.preventDefault();
        navigate(DASHBOARD_PATH);
    };

    const closeSearch = () => {
        setOpen(false);
        triggerRef.current?.focus();
    };

    // Focus the field on open and close on Escape. This overlay is plain React on purpose:
    // a Bootstrap modal would put `overflow: hidden` on <body> and risk the scroll-lock bug again.
    useEffect(() => {
        if (!isOpen) return undefined;
        inputRef.current?.focus();
        const onKey = (e) => { if (e.key === 'Escape') closeSearch(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const hasQuery = searchTerm.trim().length > 0;

    const searchButton = (
        <button
            ref={triggerRef}
            type="button"
            className="los-hdr-search-btn"
            onClick={() => setOpen(true)}
            aria-label={hasQuery ? 'Search FAQs (filter active)' : 'Search FAQs'}
            aria-haspopup="dialog"
        >
            <SearchIcon />
            {hasQuery && <span className="los-hdr-search-dot" aria-hidden="true" />}
        </button>
    );

    return (
        <div className="position-relative">
            <style>{HEADER_STYLES}</style>
            {/* app.css has a global `header { position: fixed; width: 100% }`. Override it here so this
                header stays in document flow and cannot cover the FAQ content below it. */}
            <header className="rounded-0" style={{ position: 'relative' }}>
                <div className="container-fluid topNav-dash">
                    <div className="web-header">
                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                            <div className="d-flex align-items-center tablet-content">
                                <a href={DASHBOARD_PATH} onClick={goToDashboard} aria-label="Go to dashboard">
                                    <ImageComponent fileName="cholacd.svg" alt="Chola CD" style={{ height: '40px' }} className="img-fluid" />
                                </a>
                                <h6 className="Pname">CD</h6>
                            </div>
                            {setSearchTerm && searchButton}
                        </div>
                    </div>
                    <div className="mob-header">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="text-white fs-6 d-flex py-2 mt-2 align-items-center gap-1">
                                <button
                                    className="icon-arrowleftl1 fs-2 btn p-0 border-0 bg-transparent text-white"
                                    onClick={goToDashboard}
                                    type="button"
                                    aria-label="Go back to dashboard"
                                    style={{ cursor: 'pointer' }}
                                />
                                {title}
                            </div>
                            {setSearchTerm && <div className="mt-2">{searchButton}</div>}
                        </div>
                    </div>
                </div>
            </header>

            {isOpen && setSearchTerm && (
                <div className="los-hdr-search" role="dialog" aria-modal="true" aria-label="Search FAQs">
                    <div className="los-hdr-search-backdrop" onClick={closeSearch} />
                    <div className="los-hdr-search-panel">
                        <div className="los-hdr-search-row">
                            <SearchIcon size={16} />
                            <input
                                ref={inputRef}
                                type="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); closeSearch(); } }}
                                placeholder={searchPlaceholder}
                                aria-label="Search FAQ questions and answers"
                            />
                            {searchTerm && <button type="button" onClick={() => { setSearchTerm(''); inputRef.current?.focus(); }}>Clear</button>}
                            <button type="button" onClick={closeSearch}>Done</button>
                        </div>
                        <p className="los-hdr-search-hint">Searches every category. Results update as you type.</p>
                    </div>
                </div>
            )}
        </div>
    );
};

Header.propTypes = {
    searchTerm: PropTypes.string,
    setSearchTerm: PropTypes.func,
    showSearchModal: PropTypes.bool,
    setShowSearchModal: PropTypes.func,
    title: PropTypes.string,
    searchPlaceholder: PropTypes.string,
};

export default Header;
